import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import {
  AccessMode,
  DeploymentTypeEnum,
  JoinRequestStatus,
  type MatchingDomainOrgDto,
} from "@superblocksteam/shared";

import logo from "assets/images/logo.svg";
import { SUPERBLOCKS_UI_DEPLOYMENT_TYPE } from "env";
import { useFeatureFlag } from "hooks/ui";
import { LOGOUT_URL } from "legacy/constants/routes";
import {
  getAccessMode,
  getCurrentUser,
  getIsSuperUser,
  getJoinRequestStatus,
  getMatchingDomainOrg,
} from "legacy/selectors/usersSelectors";
import { Flag } from "store/slices/featureFlags";
import { callServer, HttpMethod } from "store/utils/client";

import {
  buildAckKey,
  readAck,
  resolveEffectiveStatus,
  writeAck,
} from "./ackKey";

// Overlay + Card styling mirrors EmailVerificationWrapper so the two gates look
// like the same family of screens during onboarding. Keep these tokens in sync
// when updating either file.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fbfaf9;
  backdrop-filter: blur(10px);
  z-index: 100;
`;

const Card = styled.div`
  width: 756px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0px 0px 0px 1px rgba(14, 20, 27, 0.16),
    0px 6px 12px 0px rgba(14, 20, 27, 0.06);
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 48px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
`;

const LogoWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 9px;
  background: linear-gradient(135deg, #575757 0%, #000000 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const LogoImg = styled.img`
  width: 32px;
  height: 32px;
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  width: 560px;
  max-width: 100%;
`;

const Heading = styled.h1`
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: #131416;
  margin: 0;
`;

const Subtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #767a7f;
  margin: 0;
`;

const OrgHighlight = styled.span`
  color: #131416;
  font-weight: 500;
`;

const BodySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

// Notice-style banner shared by the PENDING and DENIED states. Variant colors
// match the tokens used in EmailVerificationWrapper (success = #52c41a, error =
// #FF3341) and add a warning token (#FAAD14) for the "you'll lose personal
// access" nudge on the initial request card.
const NoticeWrapper = styled.div<{ $type: "success" | "error" | "warning" }>`
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  border: 1px solid
    ${({ $type }) =>
      $type === "error"
        ? "#FF3341"
        : $type === "warning"
          ? "#FAAD14"
          : "#52c41a"};
  background-color: ${({ $type }) =>
    $type === "error"
      ? "rgba(255, 51, 65, 0.03)"
      : $type === "warning"
        ? "rgba(250, 173, 20, 0.06)"
        : "rgba(82, 196, 26, 0.03)"};
  color: ${({ $type }) =>
    $type === "error"
      ? "#FF3341"
      : $type === "warning"
        ? "#8A5A00"
        : "#389e0d"};
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  background-color: #131416;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 17px;
  line-height: 24px;
  cursor: pointer;
  position: relative;
  box-shadow:
    0px 0px 0px 1px black,
    0px 1px 2px 0px rgba(27, 30, 34, 0.08),
    0px 3px 8px 1px rgba(14, 20, 27, 0.16);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0px 1px 0px 0px rgba(255, 255, 255, 0.3);
    pointer-events: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  border: 1px solid rgba(14, 20, 27, 0.16);
  border-radius: 10px;
  background-color: white;
  color: #131416;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 17px;
  line-height: 24px;
  cursor: pointer;

  &:hover {
    background-color: #fbfaf9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div<{ $horizontal?: boolean }>`
  display: flex;
  flex-direction: ${({ $horizontal }) => ($horizontal ? "row" : "column")};
  gap: 12px;
  width: 100%;
`;

const LogoutLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #767a7f;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: #caccce;

  &:hover {
    color: #4d5156;
  }
`;

const JoinOrgGateCustom = ({ children }: { children: React.ReactNode }) => {
  const matchingDomainOrg = useSelector(getMatchingDomainOrg) as
    | MatchingDomainOrgDto
    | null
    | undefined;
  const joinRequestStatus = useSelector(getJoinRequestStatus);
  const accessMode = useSelector(getAccessMode);
  const isSuperUser = useSelector(getIsSuperUser);
  const currentUser = useSelector(getCurrentUser);
  const joinOrgGateEnabled = useFeatureFlag(Flag.ENABLE_JOIN_ORG_GATE);

  // Seed local state from the server-provided status so the right card renders
  // on first paint (and we don't flash the "Join your team" prompt for a user
  // who already has a PENDING/DENIED record). `requestSent` drives the PENDING
  // card; once we call POST /v1/join-requests we flip it locally so we don't
  // need to wait for a /me refetch.
  const [requestSent, setRequestSent] = useState(
    joinRequestStatus === JoinRequestStatus.PENDING,
  );
  // Local cancelled-this-session flag. Server row flips to CANCELLED on the cancel POST,
  // but the Redux-backed `joinRequestStatus` still reports PENDING until the next /me
  // refetch. We override the effective status to CANCELLED (not null) so the ackKey stays
  // under `...CANCELLED:<orgId>` — write and read keys line up across a Continue click,
  // and the stale server PENDING can't leak back through once the override is cleared.
  const [locallyCancelled, setLocallyCancelled] = useState(false);

  // Effective status drives the card we render. Local `requestSent` and
  // `locallyCancelled` override server state during the window between the
  // mutation and the next /me refetch.
  const effectiveStatus = locallyCancelled
    ? JoinRequestStatus.CANCELLED
    : resolveEffectiveStatus(requestSent, joinRequestStatus);
  const ackKey = buildAckKey(
    currentUser?.id,
    effectiveStatus,
    matchingDomainOrg?.id,
  );
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  // Initial `skipped` is whatever the current `ackKey` resolves to, but /me often hasn't
  // returned on first paint — so `ackKey` starts out as the "anon:none:none" placeholder and
  // the real key only materializes a render later. Keep it in sync via an effect so the
  // persisted dismissal is honored once the real key is known; otherwise the card would
  // re-appear on every reload.
  const [skipped, setSkipped] = useState(() => readAck(ackKey));
  useEffect(() => {
    setSkipped(readAck(ackKey));
  }, [ackKey]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const requestDenied = effectiveStatus === JoinRequestStatus.DENIED;
  const isPending = effectiveStatus === JoinRequestStatus.PENDING;
  const emailDomain = currentUser?.email?.split("@")[1] ?? "";

  const handleRequestJoin = useCallback(async () => {
    if (!matchingDomainOrg) return;
    setLoading(true);
    setErrorMessage(undefined);
    try {
      await callServer({
        method: HttpMethod.Post,
        url: "v1/join-requests",
        body: { organizationId: matchingDomainOrg.id },
      });
      // Clear the locally-cancelled override; otherwise `effectiveStatus` stays
      // forced to null and we keep rendering the initial card instead of the
      // "Request sent" PENDING card until the next /me refetch.
      setLocallyCancelled(false);
      setRequestSent(true);
    } catch (e) {
      setErrorMessage(
        e instanceof Error
          ? e.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [matchingDomainOrg]);

  const handleSkip = useCallback(() => {
    writeAck(ackKey);
    setSkipped(true);
  }, [ackKey]);

  const handleCancel = useCallback(async () => {
    if (!matchingDomainOrg) return;
    setCancelLoading(true);
    setErrorMessage(undefined);
    try {
      await callServer({
        method: HttpMethod.Post,
        url: "v1/join-requests/cancel",
        body: { organizationId: matchingDomainOrg.id },
      });
      // Flip local state back to the initial card immediately. `requestSent` must
      // drop too, otherwise `resolveEffectiveStatus` still returns PENDING until
      // the next /me refetch lands.
      setRequestSent(false);
      setLocallyCancelled(true);
    } catch (e) {
      setErrorMessage(
        e instanceof Error
          ? e.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setCancelLoading(false);
    }
  }, [matchingDomainOrg]);

  const handleLogout = useCallback(() => {
    window.location.href = LOGOUT_URL;
  }, []);

  if (
    SUPERBLOCKS_UI_DEPLOYMENT_TYPE === DeploymentTypeEnum.CLOUD_PREM ||
    accessMode === AccessMode.VISITOR ||
    accessMode === AccessMode.EXTERNAL_USER ||
    isSuperUser ||
    !joinOrgGateEnabled ||
    !matchingDomainOrg ||
    // APPROVED is a terminal state: either the admin used /approve (switchUserOrg already ran,
    // so the user is in the target org and the gate is unreachable for that session) or the
    // admin used the invite path (row flipped to APPROVED, transfer invite email dispatched,
    // user still in personal workspace). In the invite case `matchingDomainOrg` is still
    // populated and `joinRequestStatus` is APPROVED — without this bypass the user would land
    // on the initial "Request to join" card, which would idempotently return the same APPROVED
    // row on click and loop forever with no visible feedback. Treat APPROVED as "stop blocking
    // the user; their invite is already in their inbox".
    effectiveStatus === JoinRequestStatus.APPROVED ||
    // PENDING always blocks: a user with a pending request must not be able to use a prior
    // `skipped` ack to continue into their personal workspace. They either wait for the admin
    // or cancel. Any other status (none / DENIED / CANCELLED) respects the ack — and since
    // `locallyCancelled` keys under `...CANCELLED:<orgId>` (distinct from the `...none:...`
    // ack written from a "Continue with personal" click on the very first card), a pre-cancel
    // skip can't slip the user past a post-cancel gate.
    (skipped && !isPending)
  ) {
    return <>{children}</>;
  }

  const antIcon = <LoadingOutlined style={{ fontSize: 20 }} spin />;

  // PENDING: admin hasn't reviewed yet. User cannot bypass the gate while their
  // request is open — they either wait for admin action, cancel the request, or
  // sign out. Cancelling invalidates the row (admin sees it but can't approve)
  // and returns the user to the initial card so they can re-request or skip.
  if (isPending) {
    return (
      <Overlay>
        <Card>
          <CardBody>
            <HeaderSection>
              <LogoWrapper>
                <LogoImg src={logo} alt="Superblocks" />
              </LogoWrapper>
              <TextGroup>
                <Heading>Request sent</Heading>
                <Subtitle>
                  Your request to join{" "}
                  <OrgHighlight>{matchingDomainOrg.name}</OrgHighlight> has been
                  sent to the Admin.
                </Subtitle>
              </TextGroup>
            </HeaderSection>

            <BodySection>
              <NoticeWrapper $type="success" data-test="join-org-gate-pending">
                Once the Admin approves your request, you&apos;ll be moved into{" "}
                <OrgHighlight>{matchingDomainOrg.name}</OrgHighlight>. Until
                then, you&apos;ll see this screen whenever you sign in. Cancel
                the request if you want to start in a personal workspace.
              </NoticeWrapper>
              {errorMessage && (
                <NoticeWrapper $type="error">{errorMessage}</NoticeWrapper>
              )}
              <ButtonGroup>
                <SecondaryButton
                  onClick={handleCancel}
                  disabled={cancelLoading}
                  data-test="join-org-cancel-btn"
                >
                  {cancelLoading ? (
                    <Spin indicator={antIcon} />
                  ) : (
                    "Cancel request"
                  )}
                </SecondaryButton>
              </ButtonGroup>
            </BodySection>
          </CardBody>
        </Card>

        <LogoutLink
          onClick={handleLogout}
          style={{ position: "absolute", bottom: 24, right: 24 }}
        >
          Sign out
        </LogoutLink>
      </Overlay>
    );
  }

  // DENIED: admin rejected an earlier request. Don't offer a re-request button —
  // the API also rejects the POST in this state (ForbiddenError), so hiding the
  // button keeps UI and backend aligned.
  if (requestDenied) {
    return (
      <Overlay>
        <Card>
          <CardBody>
            <HeaderSection>
              <LogoWrapper>
                <LogoImg src={logo} alt="Superblocks" />
              </LogoWrapper>
              <TextGroup>
                <Heading>Request declined</Heading>
                <Subtitle>
                  Your request to join{" "}
                  <OrgHighlight>{matchingDomainOrg.name}</OrgHighlight> was
                  declined by an Admin.
                </Subtitle>
              </TextGroup>
            </HeaderSection>

            <BodySection>
              <NoticeWrapper $type="error" data-test="join-org-gate-denied">
                If you believe this was a mistake, please reach out to an Admin
                directly. You can keep using your personal workspace.
              </NoticeWrapper>
              <ButtonGroup>
                <PrimaryButton
                  onClick={handleSkip}
                  data-test="join-org-denied-continue-btn"
                >
                  Continue to my workspace
                </PrimaryButton>
              </ButtonGroup>
            </BodySection>
          </CardBody>
        </Card>

        <LogoutLink
          onClick={handleLogout}
          style={{ position: "absolute", bottom: 24, right: 24 }}
        >
          Sign out
        </LogoutLink>
      </Overlay>
    );
  }

  // Initial: user has not requested yet. Warn explicitly that approval replaces
  // the personal workspace, since today the approve endpoint moves the user's
  // only org membership over to the team org (switchUserOrg semantics).
  return (
    <Overlay>
      <Card>
        <CardBody>
          <HeaderSection>
            <LogoWrapper>
              <LogoImg src={logo} alt="Superblocks" />
            </LogoWrapper>
            <TextGroup>
              <Heading>Your team is using Superblocks</Heading>
              <Subtitle>
                There&apos;s an existing Superblocks organization under the{" "}
                <OrgHighlight>{emailDomain}</OrgHighlight> domain. Would you
                like to join this organization?
              </Subtitle>
            </TextGroup>
          </HeaderSection>

          <BodySection>
            <NoticeWrapper $type="warning" data-test="join-org-gate-warning">
              Request to join your team (requires Admin approval), or start
              fresh in a personal workspace.
            </NoticeWrapper>
            {errorMessage && (
              <NoticeWrapper $type="error">{errorMessage}</NoticeWrapper>
            )}
            <ButtonGroup $horizontal>
              <SecondaryButton
                onClick={handleSkip}
                data-test="join-org-skip-btn"
              >
                Continue with personal workspace
              </SecondaryButton>
              <PrimaryButton
                onClick={handleRequestJoin}
                disabled={loading}
                data-test="join-org-request-btn"
              >
                {loading ? <Spin indicator={antIcon} /> : "Request to join"}
              </PrimaryButton>
            </ButtonGroup>
          </BodySection>
        </CardBody>
      </Card>

      <LogoutLink
        onClick={handleLogout}
        style={{ position: "absolute", bottom: 24, right: 24 }}
      >
        Sign out
      </LogoutLink>
    </Overlay>
  );
};

export default JoinOrgGateCustom;
